import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { JourneyDetail, JourneyTaskRow } from "./types";

export type ReorderTasksInput = {
  journeyId: string;
  taskIds: string[];
};

type Context = {
  previousDetail: JourneyDetail | undefined;
};

// PATCH /api/admin/journeys/:journeyId/tasks/order
//
// Replaces /app/admin/journeys/_actions.ts::reorderJourneyTasksAction.
// Optimistic — rebuilds detail.tasks from the new ordering using a flat
// taskId map, then re-numbers `position` 0..N-1 so the local cache matches
// what the worker will write. One PATCH per drop (the bucket-5 DnD
// convention; flat array because journey tasks have no grouping hierarchy).
export function useReorderTasks() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ReorderTasksInput, Context>({
    mutationFn: ({ journeyId, taskIds }) =>
      apiFetch<{ ok: true }>(`/admin/journeys/${journeyId}/tasks/order`, {
        method: "PATCH",
        body: JSON.stringify({ taskIds }),
      }),
    onMutate: async ({ journeyId, taskIds }) => {
      const detailKey = queryKeys.journeys.detail(journeyId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<JourneyDetail>(detailKey);
      queryClient.setQueryData<JourneyDetail>(detailKey, (old) => {
        if (!old) return old;
        const taskById = new Map(old.tasks.map((t) => [t.id, t] as const));
        const nextTasks = taskIds
          .map((id) => taskById.get(id))
          .filter((t): t is JourneyTaskRow => t !== undefined);
        // Drop the optimistic reorder on the floor if any id is unknown or
        // any existing id is missing — the onSettled invalidate refetches
        // and the server's validation surfaces the real error via the toast.
        if (nextTasks.length !== old.tasks.length) return old;
        return {
          ...old,
          tasks: nextTasks.map((t, idx) => ({ ...t, position: idx })),
        };
      });
      return { previousDetail };
    },
    onError: (err, { journeyId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.journeys.detail(journeyId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to reorder tasks");
    },
    onSettled: (_data, _err, { journeyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(journeyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
  });
}
